import {
  defaultSitePaginationConfig,
  type CDLQueryParams,
  type RecordList,
  type Site,
  type RecordEntityId,
  type DeliveryDestination,
  type LocalMenuGroupToSiteRelationship,
  P2Role,
  type DeliveryDestinationResponse,
  type StationBrand,
  type SiteLocation,
  CountryCode,
  type Multigroup,
  type SitesUsingPriceLevelResponse,
  IntegrationPartner,
} from '@centric-os/types';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { defineStore, storeToRefs, type StateTree } from 'pinia';
import { useAuthStore } from '../../auth';
import { isEmpty } from 'lodash-es';
import { useKDSStore, useMultigroupStore } from '..';

interface State extends StateTree {
  site: Site;
  sites: Site[];
  siteDeliveryDestinations: DeliveryDestination[];
  updateSiteOnNextMount: boolean;
  siteLocalMenuGroups: LocalMenuGroupToSiteRelationship[];
  sitesUsingPriceLevel: SitesUsingPriceLevelResponse['sites'];
  // should be temporaly
  loadingAllSites: boolean;
  editSite: boolean;
}
export function useSiteStore(storeId = 'site') {
  const store = defineStore(storeId, {
    state: (): State => ({
      site: null,
      sites: [],
      siteDeliveryDestinations: [],
      updateSiteOnNextMount: false,
      siteLocalMenuGroups: [],
      sitesUsingPriceLevel: [],
      // should be temporaly
      loadingAllSites: false,
      editSite: false,
    }),
    actions: {
      async getSites(
        noPagination: boolean = false,
        params: CDLQueryParams = {
          sortBy: 'created_at',
          sortOrder: 'DESC',
        },
        config: AxiosRequestConfig = {},
      ): Promise<void> {
        // Stores for user config
        const authStore = useAuthStore();
        const multigroupStore = useMultigroupStore();

        const isSiteOperator = authStore.hasRole(P2Role.SITE_OPERATOR);

        // Fetch multigroups if user is not site operator
        if (!isSiteOperator) {
          await multigroupStore.getMultiGroups();
        }

        if (!noPagination) {
          params = this.pagination.queryParams;
        }

        if (
          !isEmpty(multigroupStore.getMultigroupIDs) &&
          !isEmpty(params) &&
          isEmpty(params.id_multigroups)
        ) {
          params = { ...params, id_multigroups: multigroupStore.getMultigroupIDs };
        }

        const response = await this.cdlApi.get<RecordList<Site>>('/centricos/sites', {
          ...config,
          params,
        });
        const { results, meta } = response.data;

        this.sites = results;

        if (!noPagination) {
          this.updatePaginationWithMeta(meta);
        }
      },
      resetAndClearFilter(): void {
        // Resets and clears the site search query
        this.$reset();
        delete this.pagination.queryParams.filter;
      },
      async getSite(siteId: RecordEntityId, extended = true): Promise<void> {
        const response = await this.cdlApi.get<Site>(`/location/group/${siteId}?nocache=1`, {
          params: { extended: extended, include_estimated_wait_time: false },
        });
        this.site = response.data;
      },
      async getSiteDetailsPage(siteId: RecordEntityId, extended = true): Promise<void> {
        await this.cdlApi
          .get<Site>(`/location/group/${siteId}?nocache=1`, {
            params: { extended: extended, include_estimated_wait_time: false },
          })
          .then(async (response) => {
            this.site = response.data;

            // Fetch KDS devices for the site BUs
            const kdsStore = useKDSStore();
            await kdsStore.fetchKDSDevices();

            // Fetch delivery destinations for the site
            await Promise.all([
              this.getSiteDeliveryDestinations(siteId),
              this.getSitesLocalMenuGroups(siteId),
            ]);
          });
      },
      async getSiteNoExtended(siteId: RecordEntityId): Promise<Site> {
        const response = await this.cdlApi.get<Site>(
          `/location/group/${siteId}?nocache=1&include_estimated_wait_time=false`,
        );
        return response?.data;
      },
      async updateSite(siteId: RecordEntityId, payload: Partial<Site>): Promise<void> {
        const response = await this.cdlApi.patch<Site>(`/location/group/${siteId}`, payload);
        this.site = response.data;
      },
      async updateLocationSequence(
        id: RecordEntityId,
        payload: Partial<SiteLocation>,
      ): Promise<void> {
        const response = await this.cdlApi.patch<SiteLocation>(`/location/${id}`, payload);
        const updatedLocation = response.data;
        const index = this.site.locations.findIndex(
          (location) => location.id === updatedLocation.id,
        );
        if (index !== -1) {
          this.site.locations[index] = updatedLocation;
        }
      },
      async getSiteDeliveryDestinations(siteId: RecordEntityId): Promise<void> {
        const response = await this.cdlApi.get<DeliveryDestinationResponse>(
          `/location/group/${siteId}/deliverydestination`,
        );

        // Sort the delivery destinations by sort_sequence without mutating the original array
        const sortedLocations = response?.data?.delivery_destinations?.toSorted(
          (a: DeliveryDestination, b: DeliveryDestination) => a.sort_sequence - b.sort_sequence,
        );

        this.siteDeliveryDestinations = sortedLocations;
      },
      async getSitesLocalMenuGroups(
        siteId: RecordEntityId,
      ): Promise<LocalMenuGroupToSiteRelationship[]> {
        const response = await this.cdlApi.get<RecordList<LocalMenuGroupToSiteRelationship>>(
          `/menu/v3/local-menu-group/${siteId}/site`,
        );
        this.siteLocalMenuGroups = response?.data?.results;
        return this.siteLocalMenuGroups;
      },
      //  This method should be temporaly
      async getSitesForUser(): Promise<void> {
        this.loadingAllSites = true;
        // Stores for user config
        const authStore = useAuthStore();
        const { cdlUser } = storeToRefs(authStore);
        const multigroupStore = useMultigroupStore();
        const { multigroups } = storeToRefs(multigroupStore);

        // Fetch multigroups if user is not site operator
        if (isEmpty(multigroups.value)) {
          await multigroupStore.getMultiGroups();
        }

        const promises = multigroups.value?.map((multigroup) =>
          this.getUserMultigroupSites(multigroup.id, cdlUser.value?.id),
        );

        // Wait for all promises to resolve
        const results = await Promise.all(promises);

        // Flatten the array of arrays into a single array
        const flattenedResultsArray = results.flat().filter((result) => result !== null);

        this.sites = flattenedResultsArray;
        this.loadingAllSites = false;
      },
      // This method should be temporaly
      async getUserMultigroupSites(multigroupId: string, userId: string): Promise<Site[]> {
        const response = await this.cdlApi.get<any>(`/location/multigroup/${multigroupId}`);

        return response.data?.groups;
      },

      /**
       * @returns true if the site is Canadian, false otherwise
       */
      isCanadianSite(): boolean {
        return this.site?.address?.country?.toLowerCase() === CountryCode.CA.toLowerCase();
      },
      async createSite(payload: Partial<Site>): Promise<Site> {
        const response = await this.cdlApi.post<Site>('/location/group', payload);
        this.site = response.data;
        return this.site;
      },
      async addSiteToMultigroup(payload: Partial<Multigroup>): Promise<void> {
        await this.cdlApi.patch(`/location/multigroup/${payload.id}`, payload);
      },
      async getSitesForPriceLevel(priceLevelId: RecordEntityId): Promise<void> {
        const response = await this.cdlApi.get<SitesUsingPriceLevelResponse>(
          `/centricos/sites/price-level/${priceLevelId}`,
        );
        this.sitesUsingPriceLevel = response.data?.sites;
      },
      async updateBulkBrands(payload: Partial<StationBrand>) {
        if (payload) {
          await this.cdlApi.patch<
            StationBrand,
            AxiosResponse<StationBrand[], any>,
            Partial<StationBrand>
          >(`/location/brand/bulk`, {
            ...payload,
          });
        } else {
          return Promise.reject(new Error('no payload selected'));
        }
      },

      setSiteStations(payload: { locationId: string; brands: StationBrand[] }) {
        if (payload) {
          const location = this.site.locations.filter((loc) => loc.id === payload.locationId)[0];
          location.brands = payload.brands;
        }
      },

      async deleteSite(siteId: RecordEntityId, multigroupId: RecordEntityId): Promise<void> {
        await this.cdlApi.delete(`/location/multigroup/${multigroupId}`, {
          data: { groups: [{ id: siteId }] },
        });
      },
    },
    getters: {
      hasThriveBoostIntegration: (state: State): boolean => {
        const thriveBoostIntegrationTypes = new Set([
          IntegrationPartner.BOOST.toLowerCase(),
          IntegrationPartner.THRIVE.toLowerCase(),
        ]);

        return state.site?.integrations?.some(
          (integration: string) => thriveBoostIntegrationTypes.has(integration.toLowerCase())
        );
      },
      sitesStations: (state: State): StationBrand[] => {
        return (
          state.site?.locations?.flatMap((location) => {
            return location.brands;
          }) || []
        );
      },
      sitesLocationMap: (state: State): Record<RecordEntityId, SiteLocation> => {
        return state.site?.locations?.reduce(
          (acc: Record<RecordEntityId, SiteLocation>, val: SiteLocation) => {
            acc[val.id] = val;
            return acc;
          },
          {},
        );
      },
      isUnlistedSite: (state: State): boolean =>
        state.site?.locations?.[0]?.location_multigroup ===
        import.meta.env.VITE_UNLISTED_MULTIGROUP,
    },
    pagination: true,
    paginationConfig: {
      ...defaultSitePaginationConfig,
    },
  });
  return store();
}
